import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/features/Auth/lib/useAuth';
import { useWorkspace } from '@/features/Workspace/lib/useWorkspace';
import { useDashboard } from '@/features/Sale/Dashboard/lib/useDashboard';
import { Button } from '@/shared/components/common/Button/Button';
import Popup from '@/shared/components/layers/Popup/Popup';
import type { DepartmentInterface, UserInterface } from '@/shared/model/api.types';
import type { SalesPeriod } from './types';
import { historyError, salesHistoryApi } from './api';
import { canManageDepartment, formatDate, HISTORY_UPDATED, initialPeriod, money, todayInMoscow } from './utils';
import { isSalesSystemAccount, salesAdministrationDepartments } from '@/features/Auth/lib/salesAccess';
import { useReport } from './useReport';
import { ReportView } from './ReportView';
import { TransferForm } from './TransferForm';
import { SnapshotComparison } from './SnapshotComparison';
import SnapshotAnalytics from './SnapshotAnalytics';
import css from './PlanHistory.module.css';

export type PlanHistoryMode = 'snapshot' | 'analytics' | 'transfer';
export type PlanHistoryActionMode = Exclude<PlanHistoryMode, 'analytics'>;

export default function PlanHistoryPopup({ mode, onClose }: { mode: PlanHistoryMode; onClose: () => void }) {
  const { user } = useAuth();
  const { departments, users, loading, error } = useWorkspace();
  const { dateStart, dateEnd, selectedDepartment } = useDashboard();
  const managed = isSalesSystemAccount(user)
    ? salesAdministrationDepartments(departments)
    : departments.filter(d => canManageDepartment(user, d.id));
  if (!managed.length) return <Popup open onClose={onClose}>
    <p>Нет доступных отделов для управления планом.</p>
    <Button onClick={onClose}>Назад к планам</Button>
  </Popup>;
  if (mode === 'analytics') return <SnapshotAnalytics departments={managed} onClose={onClose}/>;
  const departmentId = managed.find(d => d.id === Number(selectedDepartment))?.id ?? managed[0].id;
  return <HistoryDialog mode={mode} departments={managed} users={users} usersLoading={loading.users} usersError={error.users}
    initial={initialPeriod(departmentId, dateStart, dateEnd)} onClose={onClose}/>;
}

type DialogProps = {
  mode: PlanHistoryActionMode;
  departments: DepartmentInterface[];
  users: UserInterface[];
  usersLoading: boolean;
  usersError: string | null;
  initial: SalesPeriod;
  onClose: () => void;
};

export function HistoryDialog({ mode, departments, users, usersLoading, usersError, initial, onClose }: DialogProps) {
  const [period, setPeriod] = useState<SalesPeriod>(initial);
  const [revision, setRevision] = useState(0);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const mounted = useRef(false);
  const dialog = useRef<HTMLDivElement>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [confirmSnapshot, setConfirmSnapshot] = useState(false);
  const { data: report, loading, error: readError } = useReport(period, revision);
  const today = todayInMoscow();
  const maySnapshot = !!report && !report.snapshot && report.dateEnd < today;

  useEffect(() => {
    mounted.current = true;
    const previous = document.activeElement as HTMLElement | null;
    dialog.current?.focus();
    return () => { mounted.current = false; previous?.focus(); };
  }, []);

  const changePeriod = (next: SalesPeriod) => {
    setPeriod(next); setConfirmSnapshot(false); setMessage(''); setError('');
  };

  const mutate = async (operation: () => Promise<string>) => {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true); setError(''); setMessage(''); setConfirmSnapshot(false);
    try {
      const result = await operation();
      if (mounted.current) setMessage(result);
      // No polling. Existing dashboard cards refetch once after an explicit successful write.
      window.dispatchEvent(new Event(HISTORY_UPDATED));
    } catch (err) {
      if (mounted.current) setError(historyError(err));
    } finally {
      busyRef.current = false;
      if (mounted.current) { setBusy(false); setRevision(n => n + 1); }
    }
  };

  return <Popup open onClose={() => { if (!busyRef.current) onClose(); }} size="xl">
    <div className={css.dialog} role="dialog" aria-modal="true" aria-labelledby="plan-history-title" tabIndex={-1} ref={dialog}
      onKeyDown={e => {
        if (e.key === 'Escape' && !busyRef.current) { e.stopPropagation(); onClose(); }
        if (e.key !== 'Tab') return;
        const focusable = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), select:not(:disabled), input:not(:disabled), summary') ?? [])
          .filter(el => !el.matches(':disabled') && el.getClientRects().length > 0);
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (!first) { e.preventDefault(); return; }
        if (e.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }}>
      <div className={css.heading}>
        <h2 id="plan-history-title">{mode === 'snapshot' ? 'Срез плана продаж' : 'Передача плана'}</h2>
        <Button variant="secondary" size="s" disabled={busy} onClick={onClose}>Закрыть</Button>
      </div>
      {mode === 'snapshot' ? <p>Основные планы продаж по обороту и прибыли. Выберите полный месяц или квартал.
        Срез сохраняет данные на момент ручной фиксации, а не восстанавливает их задним числом.
        Неделя, год и произвольные даты на основной панели остаются оперативными отчётами.</p>
        : <p>Передайте остаток основных планов по обороту и прибыли новому ответственному.
          Выберите отдел и период; для квартала дополнительно укажите месяц передачи.</p>}
      <fieldset disabled={busy} className={css.fieldset}>
        <legend className={css.srOnly}>Период и действия с планом</legend>
        <div className={css.fields}>
          <label className={css.field}>Отдел
            <select value={period.departmentId} onChange={e => changePeriod({ ...period, departmentId: Number(e.target.value) })}>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </label>
          <label className={css.field}>Год
            <select value={period.year} onChange={e => changePeriod({ ...period, year: Number(e.target.value) })}>
              {Array.from({ length: 101 }, (_, i) => 2000 + i).map(year => <option key={year}>{year}</option>)}
            </select>
          </label>
          <label className={css.field}>Период
            <select value={period.period} onChange={e => changePeriod(e.target.value === 'quarter'
              ? { departmentId: period.departmentId, year: period.year, period: 'quarter', quarter: period.period === 'month' ? Math.ceil(period.month / 3) : period.quarter }
              : { departmentId: period.departmentId, year: period.year, period: 'month', month: period.period === 'quarter' ? (period.quarter - 1) * 3 + 1 : period.month })}>
              <option value="month">Месяц</option><option value="quarter">Квартал</option>
            </select>
          </label>
          <label className={css.field}>{period.period === 'month' ? 'Месяц' : 'Квартал'}
            <select value={period.period === 'month' ? period.month : period.quarter} onChange={e => changePeriod(period.period === 'month'
              ? { ...period, month: Number(e.target.value) } : { ...period, quarter: Number(e.target.value) })}>
              {Array.from({ length: period.period === 'month' ? 12 : 4 }, (_, i) => i + 1).map(n => <option key={n} value={n}>
                {period.period === 'month' ? new Date(2024, n - 1, 1).toLocaleString('ru-RU', { month: 'long' }) : `${n} квартал`}
              </option>)}
            </select>
          </label>
          <Button variant="secondary" size="s" disabled={loading} onClick={() => { setConfirmSnapshot(false); setRevision(n => n + 1); }}>Обновить отчёт</Button>
        </div>
        {loading && <p role="status">Загружаем отчёт…</p>}
        {readError && <p role="alert" className={css.error}>{readError}</p>}
        {report && <>
          {mode === 'snapshot' && <ReportView report={report}/>}
          {mode === 'snapshot' && report.snapshot && <SnapshotComparison key={JSON.stringify(period)} period={period}/>}
          {mode === 'snapshot' && !report.snapshot && <section className={css.section}>
            <h3>Зафиксировать период</h3>
            <p>Фиксация доступна после окончания периода по московскому времени.
              После неё нельзя менять срез и передавать план внутри закрытого периода.
              Дополнительные планы и прогнозы не фиксируются.</p>
            <Button disabled={!maySnapshot} onClick={() => setConfirmSnapshot(true)}>Зафиксировать {period.period === 'month' ? 'месяц' : 'квартал'}</Button>
            {!maySnapshot && <p>Выбранный период ещё не завершён.</p>}
            {confirmSnapshot && maySnapshot && <div className={css.confirm}>
              <p>Зафиксировать {report.departmentName}, {formatDate(report.dateStart)} — {formatDate(report.dateEnd)}?
                План оборота: {money(report.opportunity.target)}, план прибыли: {money(report.profit.target)}.
                Сервер повторно рассчитает данные перед сохранением. Перезаписи или удаления среза нет.</p>
              <div className={css.actions}>
                <Button variant="warning" onClick={() => void mutate(async () => {
                  const saved = await salesHistoryApi.snapshot(period);
                  return `Срез №${saved.snapshot?.id} сохранён. Данные периода зафиксированы.`;
                })}>Подтвердить фиксацию</Button>
                <Button variant="secondary" onClick={() => setConfirmSnapshot(false)}>Отмена фиксации</Button>
              </div>
            </div>}
          </section>}
          {mode === 'transfer' && report.snapshot && <p className={css.notice}>Период уже зафиксирован. Передача плана недоступна.</p>}
          {mode === 'transfer' && !report.snapshot && <TransferForm key={JSON.stringify(period)} report={report} users={users} usersLoading={usersLoading} usersError={usersError} busy={busy}
            onTransfer={input => mutate(async () => {
              const saved = await salesHistoryApi.transfer(input);
              return `План передан сотруднику ${saved.toUserName} с ${formatDate(saved.effectiveDate)}: оборот ${money(saved.remaining.opportunity)}, прибыль ${money(saved.remaining.profit)}.`;
            })}/>}
          {mode === 'transfer' && <details className={css.section}>
            <summary>История и выполнение плана</summary>
            <ReportView report={report}/>
          </details>}
        </>}
      </fieldset>
      {busy && <p role="status">Сохраняем изменение. Дождитесь подтверждения…</p>}
      {message && <p role="status" className={css.notice}>{message}</p>}
      {error && <p role="alert" className={css.error}>{error} Отчёт запрошен заново; операция автоматически не повторяется.</p>}
    </div>
  </Popup>;
}
